/**
 * Lane C0-PROPORTION, FACE-HERO-R1/R2 part 1 (structure, bald first; spec docs/review/art/FACE-HERO-R1.md): the player
 * man's and woman's skulls drawn by hand at the fine grid, one per sex and view, instead of the shared art-resolution
 * head (heads.ts `HEAD`) shrunk ×0.75 (figureB3.ts).
 * - R2 (user 10-11: the R1 square jaw looked puffy; keep the old face width and soft chin): the man's outline is the
 *   pre-skull one, measured pixel by pixel from the "before" render, with its ears; the woman's is a smaller skull (the
 *   crown a row lower, the cranium a pixel narrower each side, her own softer chin), so fronts and backs differ.
 * - Profile: brow ridge, eye socket, nose, lips a pixel behind the nose tip, a soft chin behind the lip line, the
 *   occiput trimmed; under the jaw diagonal the neck in shade, no line (R2 fix 3).
 * - 3/4: the far cheek in a pixel at the eye socket and out at the cheekbone (S4), the far jaw a pixel of shade along
 *   the outline; back 3/4: a sliver of lit cheek on the turn side and the ear.
 * - Shade (light from the upper right): a band on the dark side (two pixels on the face, one on the cranium), a rim of
 *   light on the lit edge, light on the lit forehead and cheekbone, shade under the brow ridge, under the nose and the
 *   chin; worked out per row so the left-facing views (drawn mirrored, rig/dir8.ts) keep the shadow on the screen's left.
 *   The game's finish (pixel.ts `litShade`, a cylinder shade in snapped bands) puts a hard seam down the middle of the
 *   face, so `restoreSkull` sets the skull's own skin back after the finish (figureB3.ts frameOfB3).
 * - Necks: the man's a pixel wider each side, the woman's a pixel narrower (art px, figureB3.ts `neckWidth`).
 * Only the player's adult figures (looks hero*) use it, behind `ON` until ns-art signs off; NPCs keep the shared head.
 * Grids are in fine px from the head joint's fine corner (x right, y down), the frame of faceB3.ts's features.
 */
import type { View } from '../../sprites';
import { PAD_TOP } from '../../sprites';
import { shade } from '../../palette';
import { mix } from '../faces2/crisp';
import type { Piece } from '../figure';
import type { Skeleton } from '../skeleton';

type Sex = 'm' | 'f';
/** Letters: `s` skin, `e` ear, `S` shade, `H` light, `o` dark-skin line (under the chin). */
type Cell = { x: number; y: number; ch: 's' | 'e' | 'S' | 'H' | 'o' };

let ON = true;
/** The hand-drawn hero skulls: on in the game since the user's sign-off (10-11, sample-r5); review tools can turn it off for old | new sheets. */
export function setHeroSkull(v: boolean): void {
  ON = v;
}
export const heroSkullOn = (): boolean => ON;

/**
 * Grids: x from -16 (first column) to 15, rows from `y0`; letters as `Cell`. The user's pick (10-11, mock
 * docs/review/c0-proportion/face-hero/skull-mock/r2b.png: each sex its own skull, backs down to the chin rows): the man's
 * front and 3/4 are the old head pixel for pixel (no square jaw), his side, back and back 3/4 the mock's; the woman's
 * front, 3/4, back and back 3/4 the mock's smaller skull, her profile with the man's rounder dome one row lower and a
 * pixel in (user: her side view curves like his); the man's profile chin softer and further back, nearer hers; her forehead curving back into the crown (user). Generated by the lane's skull script from the measured old outline.
 */
const GRIDS: Record<string, { y0: number; rows: readonly string[] }> = {
  'front|m': {
    y0: -14,
    rows: [
      '.............ssssss.............',
      '...........ssssssssss...........',
      '.........ssssssssssssss.........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '...esssssssssssssssssssssssse...',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '...esssssssssssssssssssssssse...',
      '.....ssssssssssssssssssssss.....',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '..........sssssssssss...........',
      '..........sssssssss.............',
    ],
  },
  'front|f': {
    y0: -13,
    rows: [
      '............ssssssss............',
      '..........ssssssssssss..........',
      '........ssssssssssssssss........',
      '........ssssssssssssssss........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '.....ssssssssssssssssssssss.....',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '...........ssssssssss...........',
      '...........ssssssssss...........',
      '.............ssssss.............',
    ],
  },
  'back|m': {
    y0: -14,
    rows: [
      '.............ssssss.............',
      '...........ssssssssss...........',
      '.........ssssssssssssss.........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '....ssssssssssssssssssssssss....',
      '.....ssssssssssssssssssssss.....',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '..........ssssssssssss..........',
      '............ssssssss............',
    ],
  },
  'back|f': {
    y0: -13,
    rows: [
      '............ssssssss............',
      '..........ssssssssssss..........',
      '........ssssssssssssssss........',
      '........ssssssssssssssss........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '..eessssssssssssssssssssssssee..',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '.....ssssssssssssssssssssss.....',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '...........ssssssssss...........',
    ],
  },
  'front3|m': {
    y0: -14,
    rows: [
      '.............ssssss.............',
      '...........ssssssssss...........',
      '.........ssssssssssssss.........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....seesssssssssssssssssssss....',
      '....seesssssssssssssssssssss....',
      '.....seessssssssssssssssssss....',
      '......seesssssssssssssssssss....',
      '......ssssssssssssssssssssss....',
      '......ssssssssssssssssssssss....',
      '.......ssssssssssssssssssss.....',
      '.......ssssssssssssssssssss.....',
      '........sssssssssssssssss.......',
      '........sssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '...........ssssssssssss.........',
      '............sssssssss...........',
      '............sssssss.............',
    ],
  },
  'front3|f': {
    y0: -13,
    rows: [
      '............ssssssss............',
      '..........ssssssssssss..........',
      '........ssssssssssssssss........',
      '........ssssssssssssssss........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....sssssssssssssssssssssss.....',
      '....seessssssssssssssssssss.....',
      '....seesssssssssssssssssssss....',
      '.....seesssssssssssssssssssss...',
      '.....sssssssssssssssssssssss....',
      '.......sssssssssssssssssssss....',
      '........sssssssssssssssssssS....',
      '.........sssssssssssssssssS.....',
      '.........sssssssssssssssssS.....',
      '...........sssssssssssssS.......',
      '...........sssssssssssssS.......',
      '............ssssssssssS.........',
      '............ssssssssssS.........',
      '............ssssssssS...........',
    ],
  },
  'back3|m': {
    y0: -14,
    rows: [
      '.............ssssss.............',
      '...........ssssssssss...........',
      '.........ssssssssssssss.........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssssH...',
      '....ssssssssssssssssssssssssH...',
      '....sssssssssssssssssssseessH...',
      '..sssssssssssssssssssssseessH...',
      '..ssssssssssssssssssssseessH....',
      '....sssssssssssssssssseessH.....',
      '.....sssssssssssssssssssssH.....',
      '.....sssssssssssssssssssssH.....',
      '.......ssssssssssssssssssH......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '...........ssssssssss...........',
      '...........ssssssssss...........',
      '............sssssss.............',
    ],
  },
  'back3|f': {
    y0: -13,
    rows: [
      '............ssssssss............',
      '..........ssssssssssss..........',
      '........ssssssssssssssss........',
      '........ssssssssssssssss........',
      '.......ssssssssssssssssss.......',
      '.......ssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssssH....',
      '....ssssssssssssssssssssssssH...',
      '....sssssssssssssssssssseessH...',
      '..sssssssssssssssssssssseessH...',
      '..ssssssssssssssssssssseessH....',
      '....ssssssssssssssssssssssH.....',
      '.....sssssssssssssssssssssH.....',
      '.....sssssssssssssssssssssH.....',
      '.......ssssssssssssssssssH......',
      '.......ssssssssssssssssss.......',
      '.........ssssssssssssss.........',
      '.........ssssssssssssss.........',
      '...........ssssssssss...........',
      '...........ssssssssss...........',
      '............sssssss.............',
    ],
  },
  'side|m': {
    y0: -14,
    rows: [
      '............ssssssss............',
      '........sssssssssssssss.........',
      '......sssssssssssssssssss.......',
      '.....sssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '....ssssssssssssssssssssssss....',
      '....ssssssssssssssssssssssss....',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....sssssssssssssssssssssssss...',
      '....ssssssssssssssssssssssssss..',
      '.....ssssssssssssssssssssssss...',
      '.....sssssssssssssssssssssss....',
      '......ssssssseesssssssssssss....',
      '.......sssssseessssssssssssss...',
      '........ssssseessssssssssssss...',
      '.........sssseesssssssssssssss..',
      '.........ssssssssssssssssssssss.',
      '..........sssssssssssssssssssss.',
      '..........sssssssssssssssssss...',
      '..........sssSsssssssssssssss...',
      '..........sssSSSssssssssssss....',
      '..........sssSSSSSsssssssssss...',
      '..........sssSSSSSSSssssssss....',
      '..........sssSSSSSSS..ssssss....',
      '..........sssSSSSSSS....sss.....',
      '..........sssSSSSSSS............',
    ],
  },
  'side|f': {
    y0: -13,
    rows: [
      '.............ssssss.............',
      '.........sssssssssssss..........',
      '.......sssssssssssssssss........',
      '......sssssssssssssssssss.......',
      '......ssssssssssssssssssss......',
      '.....ssssssssssssssssssssss.....',
      '.....ssssssssssssssssssssss.....',
      '.....sssssssssssssssssssssss....',
      '.....sssssssssssssssssssssss....',
      '.....sssssssssssssssssssssss....',
      '.....sssssssssssssssssssssss....',
      '.....sssssssssssssssssssssss....',
      '.....sssssssssssssssssssssss....',
      '.....ssssssssssssssssssssssss...',
      '.....ssssssssssssssssssssssss...',
      '......ssssssssssssssssssssss....',
      '......ssssssseesssssssssssss....',
      '.......sssssseessssssssssssss...',
      '........ssssseessssssssssssss...',
      '.........ssssssssssssssssssss...',
      '..........ssssssssssssssssssss..',
      '..........ssssssssssssssssssss..',
      '..........sssssssssssssssssss...',
      '..........sssSssssssssssssss....',
      '..........sssSSSsssssssssss.....',
      '..........sssSSSSSssssssssss....',
      '..........sssSSSSSS.sssssss.....',
      '..........sssSSSSSS...sssss.....',
      '..........sssSSSSSS.....s.......',
    ],
  },
};

/**
 * Lane FACE-EXPR jaw r1 (user 10-11: the profile chin felt pointed): the side view's lower rows with a rounder, shorter
 * chin and a fuller jaw line before it curves up to the ear; the man's squarer (the jaw's underside nearly level), the
 * woman's softer curve. No gap between the neck's shade and the jaw (user 10-11: the hole drew an outline line under the
 * chin and a notch in the neck's front); below it the throat's front carried down to the collar (fine x >= 4: the body
 * mock's profile neck, bodyMockB3.ts `mockSkullNeck`, clips the skull at x <= 3 and draws the neck behind). Rows by y;
 * the rest of the grid as today. Off until main approves.
 */
const JAW_R1: Record<Sex, Record<number, string>> = {
  m: {
    10: '..........sssSsssssssssssssss...',
    11: '..........sssSSsssssssssssss....',
    12: '..........sssSSSsssssssssssss...',
    13: '..........sssSSSSSssssssssss....',
    14: '..........sssSSSSSSSssssssss....',
    15: '..........sssSSSSSSSSSssss......',
    16: '..........sssSSSSSSSSS..........',
    17: '..........sssSSSSSSSSS..........',
    18: '..........sssSSSSSSSS...........',
    19: '..........sssSSSSSSSS...........',
    20: '..........sssSSSSSSSS...........',
    21: '..........sssSSSSSSSS...........',
  },
  f: {
    12: '..........sssSSSSsssssssssss....',
    13: '..........sssSSSSSSssssssss.....',
    14: '..........sssSSSSSSSSssssss.....',
    15: '..........sssSSSSSSSSSSsss......',
    16: '..........sssSSSSSSSS...........',
    17: '..........sssSSSSSSSS...........',
    18: '..........sssSSSSSSSS...........',
    19: '..........sssSSSSSSSS...........',
    20: '..........sssSSSSSSSS...........',
    21: '..........sssSSSSSSSS...........',
  },
};

let JAW = true;
/** Review tools: the rounder profile jaw (JAW_R1) on or off; on is the game's look since the user's OK (2026-10-11), off = the old pointed chin. */
export function setRoundJaw(v: boolean): void {
  if (JAW !== v) MEMO.clear();
  JAW = v;
}

/** The cells of a skull, shading left out (it depends on which way the frame is mirrored). */
function cellsOf(view: View, sex: Sex): Map<string, Cell> {
  const grid = GRIDS[`${view}|${sex}`]!;
  const g = new Map<string, Cell>();
  const jaw = JAW && view === 'side' ? JAW_R1[sex] : null;
  // The jaw's rows past the grid's end: the neck's front carried down behind the collar (no notch under the chin).
  const extra = jaw ? Object.keys(jaw).map(Number).filter((y) => y >= grid.y0 + grid.rows.length).sort((a, b) => a - b) : [];
  const all = [...grid.rows, ...extra.map(() => '')];
  all.forEach((given, r) => {
    const row = jaw?.[grid.y0 + r] ?? given;
    for (let k = 0; k < row.length; k++) {
      const ch = row[k]!;
      if (ch !== '.') g.set(`${k - 16},${grid.y0 + r}`, { x: k - 16, y: grid.y0 + r, ch: ch as Cell['ch'] });
    }
  });
  return g;
}

/** Shaded cells for a frame: the dark side is the screen's left, so a mirrored (left-facing) frame shades its right. */
function shaded(view: View, sex: Sex, left: boolean): Cell[] {
  const g = cellsOf(view, sex);
  const at = (x: number, y: number) => g.get(`${x},${y}`);
  const byRow = new Map<number, number[]>();
  for (const c of g.values()) {
    const row = byRow.get(c.y);
    if (row) row.push(c.x);
    else byRow.set(c.y, [c.x]);
  }
  const flip = (x: number) => (left ? -1 - x : x);
  for (const [y, xs] of byRow) {
    xs.sort((a, b) => a - b);
    // A pixel of shade on the dark side and a rim of light on the lit edge, above the jaw only (user 10-11: shade
    // blocks on the jaw sides made the face look boxy).
    if (y > 9) continue;
    const dark = at(left ? xs[xs.length - 1]! : xs[0]!, y);
    if (dark?.ch === 's') dark.ch = 'S';
    const rim = at(left ? xs[0]! : xs[xs.length - 1]!, y);
    if (rim?.ch === 's' && y >= -11) rim.ch = 'H';
  }
  const put = (pts: readonly [number, number][], ch: Cell['ch']) => {
    for (const [x, y] of pts) {
      const c = at(flip(x), y);
      if (c?.ch === 's') c.ch = ch;
    }
  };
  if (view === 'front') {
    // Light on the lit forehead and cheekbone; shade under the brow ridge (over each eye's inner end) and the nose.
    put([[6, -9], [7, -9], [7, -8], [8, -8]], 'H');
    put([[-1, 9]], 'S');
  } else if (view === 'front3') {
    put([[6, -9], [7, -9], [7, -8], [8, -8]], 'H');
    put([[5, 9]], 'S');
  } else if (view === 'side') {
    put([[6, -9], [7, -9], [7, -8], [8, -8]], 'H');
    put([[10, 9]], 'S');
  }
  return [...g.values()];
}

const MEMO = new Map<string, Cell[]>();
function cells(view: View, sex: Sex, left: boolean): Cell[] {
  const key = `${view}|${sex}|${left ? 1 : 0}`;
  let c = MEMO.get(key);
  if (!c) MEMO.set(key, (c = shaded(view, sex, left)));
  return c;
}

/** Whether this look draws its head with the hero skull: the player's man and woman (adult). */
export function heroSkullFor(look: { faceId?: string; female?: boolean } | undefined, age?: string): Sex | null {
  if (!ON || !look?.faceId?.startsWith('hero') || (age && age !== 'adult')) return null;
  return look.female ? 'f' : 'm';
}

/** The skull's colours for a skin. */
function colours(sex: Sex, skin: { skin: string; skinS: string }): Record<Cell['ch'], string> {
  // The man's ramp a step cooler (R2 fix 6).
  const S = sex === 'm' ? mix(shade(skin.skinS, -0.04), '#8a8aa0', 0.08) : skin.skinS;
  return {
    s: skin.skin,
    e: mix(skin.skin, S, 0.6),
    S: mix(skin.skin, S, 0.75),
    H: mix(skin.skin, '#fff6e8', 0.28),
    // A line of dark skin, not a red stripe (R1 sheet).
    o: mix(S, '#4a3a38', 0.3),
  };
}

/** The neck's width (art px) for a hero: the man's a pixel wider, the woman's a pixel narrower (R2 fix 1). */
export function neckWidth(_sex: Sex, given: number): number {
  // User 10-11: keep the old necks for now (the R2 narrower woman's neck read thin and long).
  return given;
}

/**
 * The head piece of the body layer for a hero: at art resolution only the art pixels wholly inside the skull (so hair,
 * hats and depth see a head there), and the whole skull at the fine grid, laid in like a hand (realism.ts: over what
 * lies behind it, under what lies in front: hair, hats, collars).
 */
export function heroSkullPiece(sk: Skeleton, sex: Sex, skin: { skin: string; skinS: string }): Piece {
  const h = sk.joints.head;
  const hx = Math.round(h.x);
  const hy = Math.round(h.y);
  const col = colours(sex, skin);
  const list = cells(sk.view, sex, !!sk.left);
  return {
    z: h.z,
    hand: true,
    draw: (g) => {
      // Art pixels: those whose four fine pixels are all skull.
      const has = new Set(list.map((c) => `${c.x},${c.y}`));
      const seen = new Set<string>();
      for (const c of list) {
        const ax = Math.floor(c.x / 2);
        const ay = Math.floor(c.y / 2);
        const k = `${ax},${ay}`;
        if (seen.has(k)) continue;
        seen.add(k);
        if (has.has(`${ax * 2},${ay * 2}`) && has.has(`${ax * 2 + 1},${ay * 2}`) && has.has(`${ax * 2},${ay * 2 + 1}`) && has.has(`${ax * 2 + 1},${ay * 2 + 1}`)) {
          g.fillStyle = skin.skin;
          g.fillRect(hx + ax, hy + ay, 1, 1);
        }
      }
    },
    fine: (g, res) => {
      if (res !== 2) return;
      for (const c of list) {
        g.fillStyle = col[c.ch];
        g.fillRect(hx * 2 + c.x, hy * 2 + c.y, 1, 1);
      }
    },
  };
}

const rgba = (hex: string) => {
  const n = parseInt(hex.slice(1, 7), 16);
  return ((255 << 24) | ((n & 255) << 16) | (n & 0xff00) | (n >> 16)) >>> 0;
};

/**
 * After the game's finish: the skull's own skin back where the finish shaded it (litShade's banded cylinder put a hard
 * seam down the middle of the face, R2 fix 6), only on pixels that still hold the skull's colour in the composed frame
 * (not under hair, features, hands). `raw` is the composed frame before the finish, `done` after (same size and placing, 2x).
 */
export function restoreSkull(
  raw: Uint32Array,
  done: CanvasRenderingContext2D,
  W: number,
  sk: Skeleton,
  sex: Sex,
  skin: { skin: string; skinS: string },
): void {
  const h = sk.joints.head;
  const X = Math.round(h.x) * 2;
  const Y = (Math.round(h.y) + PAD_TOP) * 2;
  const list = cells(sk.view, sex, !!sk.left);
  const col = colours(sex, skin);
  const x0 = X - 18;
  const y0 = Y - 18;
  const w = 38;
  const hh = 40;
  const img = done.getImageData(x0, y0, w, hh);
  const d = new Uint32Array(img.data.buffer);
  for (const c of list) {
    const gx = X + c.x;
    const gy = Y + c.y;
    const want = rgba(col[c.ch]);
    if (raw[gy * W + gx] !== want) continue;
    const i = (gy - y0) * w + (gx - x0);
    if (i < 0 || i >= d.length) continue;
    d[i] = want;
  }
  done.putImageData(img, x0, y0);
}
